import { PropAsset } from './PropAsset';

// Wall - Duvar
// Isometric stone/brick wall segment
export class Wall extends PropAsset {
  readonly id = 'wall';
  readonly width = 64;
  readonly height = 80;
  readonly walkable = false;
  readonly collisionRadius = 0.8;
  readonly occludesPlayer = true; // This prop can hide the player

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    // Wall colors based on variant
    const wallColors = [
      { main: '#6b6b6b', dark: '#4a4a4a', light: '#8a8a8a' }, // Gray stone
      { main: '#8b7355', dark: '#5a4a3a', light: '#a08570' }, // Brown brick
      { main: '#4a5a4a', dark: '#3a4a3a', light: '#6a7a6a' }, // Mossy stone
    ];
    const colors = wallColors[v % wallColors.length];

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(cx, cy - 2 * s, 25 * s, 10 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // Wall base (isometric box)
    const wallWidth = 50 * s;
    const wallHeight = 60 * s;
    const wallDepth = 16 * s;

    // Left face (darker)
    ctx.fillStyle = colors.dark;
    ctx.beginPath();
    ctx.moveTo(cx - wallWidth / 2, cy - wallDepth / 2);
    ctx.lineTo(cx - wallWidth / 2, cy - wallHeight);
    ctx.lineTo(cx, cy - wallHeight - wallDepth / 2);
    ctx.lineTo(cx, cy);
    ctx.closePath();
    ctx.fill();

    // Right face (main color)
    ctx.fillStyle = colors.main;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx, cy - wallHeight - wallDepth / 2);
    ctx.lineTo(cx + wallWidth / 2, cy - wallHeight);
    ctx.lineTo(cx + wallWidth / 2, cy - wallDepth / 2);
    ctx.closePath();
    ctx.fill();

    // Top face (lighter)
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(cx - wallWidth / 2, cy - wallHeight);
    ctx.lineTo(cx, cy - wallHeight - wallDepth / 2);
    ctx.lineTo(cx + wallWidth / 2, cy - wallHeight);
    ctx.lineTo(cx, cy - wallHeight + wallDepth / 2);
    ctx.closePath();
    ctx.fill();

    // Stone/brick pattern on front face
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1 * s;

    // Horizontal lines
    for (let i = 1; i < 4; i++) {
      const yOffset = (i / 4) * wallHeight;
      ctx.beginPath();
      ctx.moveTo(cx, cy - yOffset);
      ctx.lineTo(cx + wallWidth / 2, cy - wallDepth / 2 - yOffset);
      ctx.stroke();
    }

    // Vertical lines (staggered for brick pattern)
    for (let row = 0; row < 4; row++) {
      const yTop = cy - (row / 4) * wallHeight;
      const yBot = cy - ((row + 1) / 4) * wallHeight;
      const stagger = row % 2 === 0 ? 0 : wallWidth / 6;

      for (let col = 1; col < 3; col++) {
        const xOffset = (col / 3) * (wallWidth / 2) + stagger;
        if (xOffset < wallWidth / 2) {
          ctx.beginPath();
          ctx.moveTo(cx + xOffset, yTop - wallDepth / 2 * (xOffset / (wallWidth / 2)));
          ctx.lineTo(cx + xOffset, yBot - wallDepth / 2 * (xOffset / (wallWidth / 2)));
          ctx.stroke();
        }
      }
    }

    // Top edge highlight
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 1.5 * s;
    ctx.beginPath();
    ctx.moveTo(cx - wallWidth / 2, cy - wallHeight);
    ctx.lineTo(cx, cy - wallHeight - wallDepth / 2);
    ctx.lineTo(cx + wallWidth / 2, cy - wallHeight);
    ctx.stroke();
  }
}

// Wall Corner - Duvar Köşesi
export class WallCorner extends PropAsset {
  readonly id = 'wall_corner';
  readonly width = 64;
  readonly height = 80;
  readonly walkable = false;
  readonly collisionRadius = 0.9;
  readonly occludesPlayer = true;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    const wallColors = [
      { main: '#6b6b6b', dark: '#4a4a4a', light: '#8a8a8a' },
      { main: '#8b7355', dark: '#5a4a3a', light: '#a08570' },
      { main: '#4a5a4a', dark: '#3a4a3a', light: '#6a7a6a' },
    ];
    const colors = wallColors[v % wallColors.length];

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(cx, cy - 2 * s, 28 * s, 14 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    const size = 24 * s;
    const height = 60 * s;

    // Left wall segment
    ctx.fillStyle = colors.dark;
    ctx.beginPath();
    ctx.moveTo(cx - size, cy);
    ctx.lineTo(cx - size, cy - height);
    ctx.lineTo(cx, cy - height);
    ctx.lineTo(cx, cy);
    ctx.closePath();
    ctx.fill();

    // Right wall segment
    ctx.fillStyle = colors.main;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx, cy - height);
    ctx.lineTo(cx + size, cy - height);
    ctx.lineTo(cx + size, cy);
    ctx.closePath();
    ctx.fill();

    // Top
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(cx - size, cy - height);
    ctx.lineTo(cx, cy - height - size / 2);
    ctx.lineTo(cx + size, cy - height);
    ctx.lineTo(cx, cy - height + size / 2);
    ctx.closePath();
    ctx.fill();
  }
}
